import type { LogFilter, LogFilterSelectedRow } from '@/types';
import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { PaginationState } from '@tanstack/vue-table';
import { defineStore } from 'pinia';

export type LogState = {
    filters: LogFilter[];
    selectedRows: LogFilterSelectedRow[];
    startDate: CalendarDate;
    endDate: CalendarDate;
    openFilters: Set<string>;
    pagination: PaginationState;
};

export const useLogStore = defineStore('log', {
    state: (): LogState => ({
        filters: [
            {
                name: 'by',
                label: 'activities.filterTable.by',
            },
            {
                name: 'description',
                label: 'activities.filterTable.description',
            },
            {
                name: 'action',
                label: 'activities.filterTable.action',
            },
            {
                name: 'for',
                label: 'activities.filterTable.for',
            },
        ],
        selectedRows: [],
        startDate: today(getLocalTimeZone()).subtract({ years: 2 }),
        endDate: today(getLocalTimeZone()),
        openFilters: new Set(['by', 'description', 'action', 'for']),
        pagination: {
            pageIndex: 0,
            pageSize: 10,
        },
    }),
    getters: {
        allFiltersOpen: (state) =>
            state.filters.length > 0 && state.openFilters.size === state.filters.length,
    },
    actions: {
        updateRowSelection(rowSelection: string[], columnName: string) {
            if (rowSelection.length === 0) {
                this.selectedRows = this.selectedRows.filter((row) => row.column !== columnName);
            } else {
                this.selectedRows = this.selectedRows.filter((row) => row.column !== columnName);

                const newRows: LogFilterSelectedRow[] = rowSelection.map((value) => ({
                    value,
                    column: columnName,
                }));

                this.selectedRows.push(...newRows);
            }
        },
        toggleFilter(filterName: string) {
            if (this.openFilters.has(filterName)) {
                this.openFilters.delete(filterName);
            } else {
                this.openFilters.add(filterName);
            }
        },
        toggleAllFilters() {
            if (this.allFiltersOpen) {
                this.openFilters.clear();
            } else {
                this.filters.forEach((filter) => this.openFilters.add(filter.name));
            }
        },
        updatePagination(newPagination: PaginationState) {
            this.pagination = newPagination;
        },
    },
});
